import type { CSSProperties, ReactNode } from 'react'
import { useContent } from '@/content'
import { Button, Surface } from '@/components/ui'
import { highlight } from './highlight'
import { ResumeButton } from './ResumeButton'
import styles from './ProfileCard.module.css'

interface ProfileCardProps {
  /** Aspect ratio for the portrait frame, e.g. `'310 / 400'`. */
  imageAspect?: string
  /** `h1` on pages where this card carries the page's main heading. */
  headingLevel?: 'h1' | 'h2'
  headingId?: string
  /** Marks the portrait as the page's largest-contentful element. */
  priority?: boolean
  /**
   * The pair of actions under the tagline.
   *
   * A prop rather than a constant because the card appears on the home page
   * and on contact, where "Get in touch" is the page you are already on.
   * Always a pair, and always the same width — see `.actions` — so neither
   * reads as an afterthought.
   */
  actions?: ReactNode
  /** Phrases to lift out of the tagline, for scannability. */
  emphasise?: string[]
  className?: string
}

/**
 * Portrait, name, tagline and the two primary actions.
 *
 * The live site reuses this card in the home hero, on the contact page and in
 * the blog outro, which is why it lives here rather than inside any one of
 * them.
 */
export const ProfileCard = ({
  imageAspect = '310 / 400',
  headingLevel: Heading = 'h2',
  headingId,
  priority = false,
  actions,
  emphasise = [],
  className,
}: ProfileCardProps) => {
  const { site } = useContent()

  return (
  <Surface pad={20} radius="card" className={`${styles.card} ${className ?? ''}`}>
    <div
      className={styles.frame}
      style={{ aspectRatio: imageAspect } as CSSProperties}
    >
      <img
        src={site.portrait}
        alt={`${site.owner}, ${site.role}`}
        className={styles.portrait}
        width={1000}
        height={1500}
        {...(priority
          ? { fetchPriority: 'high' as const }
          : { loading: 'lazy' as const })}
      />
    </div>

    <div className={styles.titleBlock}>
      <Heading id={headingId} className="t-24">
        {site.owner}
      </Heading>
      <p className={`t-14 ${styles.tagline}`}>
        {highlight(site.tagline, ...emphasise)}
      </p>
    </div>

    <div className={styles.actions}>
      {actions ?? (
        <>
          {/*
            * The CV, not a booking link. A portfolio's first ask is almost
            * always "send me your CV", and the booking URL behind the old
            * button was a `mailto:` placeholder pretending to be a calendar.
            */}
          <ResumeButton />
          <Button href={`mailto:${site.email}`} variant="secondary">
            Get in touch
          </Button>
        </>
      )}
    </div>
  </Surface>
  )
}
